import { useState } from 'react';
import {
  Form,
  useNavigate,
  useNavigation,
  useActionData,
  redirect
} from 'react-router-dom';
import FileReader from 'react-native-filereader';

import classes from './Advertise.module.css';
import { getAuthToken } from '../util/auth.jsx'
import backend from '../util/backend.jsx';

import LogoImage from './LogoImage.jsx';

import { initEditState, EDIT_OPEN, EDIT_MAIN } from '../util/advertiseEditState.jsx';

function Advertise({ ad, editState, setEditState }) {

  const data = useActionData();
  const navigate = useNavigate();
  const navigation = useNavigation();

  function initSelectedLogo(ad) {
    if ('logo_contents' in ad) {
      return ad.logo_contents;
    }
    return null;
  }

  function initSelectedLogoFileType(ad) {
    if ('logo_filetype' in ad) {
      return ad.logo_filetype;
    }
    return null;
  }

  function initSelectedLogoName(ad) {
    if ('logo_name' in ad) {
      return ad.logo_name;
    }
    return null;
  }

  function initUpdateLogo(ad) {
    if ('logo_filetype' in ad) {
      return !ad.logo_filetype.startsWith('image');
    }
    return true;
  }

  function initPublicAd(ad) {
    if ('public' in ad) {
      return ad.public;
    }
    return true;
  }

  const [selectedLogo, setSelectedLogo] = useState(initSelectedLogo(ad));
  const [selectedLogoFileType, setSelectedLogoFileType] = useState(initSelectedLogoFileType(ad));
  const [selectedLogoName, setSelectedLogoName] = useState(initSelectedLogoName(ad));
  const [updateLogo, setUpdateLogo] = useState(initUpdateLogo(ad));
  const [insertLogo, setInsertLogo] = useState(initUpdateLogo(ad));
  const [publicAd, setPublicAd] = useState(initPublicAd(ad));

  const isSubmitting = navigation.state === 'submitting';

  function cancelHandler() {
    navigate('/');
  }

  function toggleUpdateLogo() {
    setUpdateLogo(!updateLogo);
    adInfoEditted();
  }

  function togglePublicAd() {
    setPublicAd(!publicAd);
    adInfoEditted();
  }

  const logoSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null;
    let image = null;
    const imageReader = new FileReader();
    imageReader.addEventListener('load', (event) => {
      image = event.target.result;
      const strImage = btoa(image);
      setSelectedLogo(strImage);
    });
    imageReader.readAsBinaryString(file);
    setSelectedLogoFileType(file.type);
    setSelectedLogoName(file.name);
    setInsertLogo(false);
    setUpdateLogo(false);
  };

  function adInfoReadOnly() {
    return !editState[EDIT_OPEN] && !editState[EDIT_MAIN];
  }

  const isDisabled = adInfoReadOnly();

  function adInfoEditted() {
    //console.log(`adInfoEditted before: ${JSON.stringify(editState)}`);
    let newState = initEditState();
    newState[EDIT_OPEN] = false;
    newState[EDIT_MAIN] = true;
    setEditState(newState);
    //console.log(`adInfoEditted newState: ${JSON.stringify(newState)}`);
    //console.log(`adInfoEditted after: ${JSON.stringify(editState)}`);
  }

  return (
    <Form method="post" className={classes.form}>
    <fieldset disabled={isDisabled}>
      {data && data.errors && (
        <ul>
          {Object.values(data.errors).map((err) => (
            <li key={err}>{err}</li>
          ))}
        </ul>
      )}
      <div>
        <p>
          <label htmlFor="title">Corporate Title</label>
          <input
            id="title"
            type="text"
            name="title"
            required
            defaultValue={ad ? ad.title : ''}
            onChange={adInfoEditted}
          />
        </p>
      </div>
      <div>
        <table>
        <tr>
        <td>
          {!insertLogo &&
            <table><tr><td>
              <input
                id="updateLogo"
                type="checkbox"
                name="updateLogo"
                checked={updateLogo}
                onChange={toggleUpdateLogo}
              />
            </td><td>
              <label htmlFor="image">Update Logo ({selectedLogoName})</label>
            </td></tr></table>
          }
          {updateLogo &&
            <>
            <label htmlFor="image">Logo File</label>
            <input type="file" name="logo" onChange={logoSelected}  accept="image/*" />
            </>
          }
        </td>
        <td>
          {ad &&
            <LogoImage
              title={ad.title}
              logo_contents={selectedLogo}
              logo_filetype={selectedLogoFileType}
            />
          }
        </td>
        </tr>
        </table>

        <input type="hidden" name="logo_contents" value={selectedLogo} />
        <input type="hidden" name="logo_filetype" value={selectedLogoFileType} />
        <input type="hidden" name="logo_name" value={selectedLogoName} />
      </div>
      <div>
        <p>
          <table><tr><td>
            <input
              id="public_check"
              type="checkbox"
              name="public_check"
              checked={publicAd}
              onChange={togglePublicAd}
            />
          </td><td>
            <label htmlFor="public">Advertisement Visible</label>
            <label htmlFor="public">(if unchecked it will not be seen by others)</label>
          </td></tr>
          </table>
          <input type="hidden" name="public" value={publicAd} />
          <label htmlFor="phone">Corporate Phone Number</label>
          <input
            id="phone"
            type="text"
            name="phone"
            required
            defaultValue={ad ? ad.phone : ''}
            onChange={adInfoEditted}
          />
        </p>
        <p>
          <label htmlFor="email">Sales email address</label>
          <input
            id="email"
            type="text"
            name="email"
            required
            defaultValue={ad ? ad.email : ''}
            onChange={adInfoEditted}
          />
        </p>
        <p>
          <label htmlFor="website">Web Site</label>
          <input
            id="website"
            type="url"
            name="website"
            required
            defaultValue={ad ? ad.website : ''}
            onChange={adInfoEditted}
          />
        </p>
        <p>
          <label htmlFor="services">Services Summary</label>
          <textarea
            id="services"
            name="services"
            rows={15}
            cols={60}
            wrap="hard"
            required
            defaultValue={ad ? ad.services : ''}
            onChange={adInfoEditted}
          />
        </p>
      </div>
      <div className={classes.actions}>
        <button type="button" onClick={cancelHandler} disabled={isSubmitting}>
          Cancel
        </button>
        <button disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
      </div>
    </fieldset>
    </Form>
  );
}

export default Advertise;

export async function action({ request, params }) {
  const method = request.method;
  const data = await request.formData();

  const adData = {
    title:         data.get('title'),
    public:        data.get('public'),
    services:      data.get('services'),
    phone:         data.get('phone'),
    email:         data.get('email'),
    website:       data.get('website'),
    logo_contents: data.get('logo_contents'),
    logo_filetype: data.get('logo_filetype'),
    logo_name:     data.get('logo_name'),
  };

  const token = getAuthToken();

  const response = await fetch(`https://${backend}/advertise`, {
    method: method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(adData),
  });

  const result = await response.json();

  if (response.status === 422) {
    return {errors: [result.message]};
  }

  if (!response.ok) {
    return {errors: ['Could not save update.']};
  }

  return redirect('/vendors');
}
